import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { matchJiraIssueKey } from '@databases/utils/confluence/jira-issue-key';
import { createElement, type ReactNode } from 'react';

import {
  defineFieldReadModule,
  type FieldReadResource,
  type FieldReadResourceState
} from '../field-read-module.ts';
import {
  EMPTY_JIRA_ISSUE_CONTENT,
  type JiraIssueReadContent,
  type JiraIssueItemContent
} from './jira-issue-read-content.ts';
import { JiraIssueValueView } from './jira-issue-value-view.tsx';

export type JiraIssueResourceInput = {
  readonly cloudId: string | undefined;
  readonly serverId: string | undefined;
  readonly key: string;
};

export type JiraIssueResourceValue = Omit<JiraIssueItemContent, 'key'>;
export type JiraIssueReadPolicy = {
  readonly authenticationLabel?: string;
  readonly getJiraIssueSnapshot?: (
    input: JiraIssueResourceInput
  ) => JiraIssueResourceValue | undefined;
  readonly getJiraIssueResource?: (
    input: JiraIssueResourceInput
  ) => FieldReadResource<JiraIssueResourceValue>;
};

export type JiraIssueReference = {
  readonly key: string;
  readonly issueKey: string;
};

function issuesFromKeys(keys: readonly string[]): readonly JiraIssueReference[] {
  return [...new Set(keys)].map((issueKey, index) =>
    Object.freeze({ key: `${issueKey}:${index}`, issueKey })
  );
}

export function getJiraIssueReferences(
  value: DatabaseValue | undefined
): readonly JiraIssueReference[] {
  switch (value?.type) {
    case DatabaseFieldType.JIRA_ISSUE:
      return value.value.flatMap((item) =>
        'key' in item && typeof item.key === 'string' && item.key
          ? [Object.freeze({ key: item._id, issueKey: item.key })]
          : []
      );
    case DatabaseFieldType.TEXT:
      return issuesFromKeys(
        value.value.plain.split(/\r\n|\r|\n/).flatMap((line) => {
          const key = matchJiraIssueKey(line);

          return key ? [key] : [];
        })
      );
    case DatabaseFieldType.HYPERLINK:
      return issuesFromKeys(
        value.value.flatMap((link) => {
          const key = matchJiraIssueKey(link.url);

          return key ? [key] : [];
        })
      );
    default:
      return [];
  }
}

function createContent(
  issues: readonly JiraIssueReference[],
  resolvedByIssueKey: ReadonlyMap<string, JiraIssueResourceValue>
): JiraIssueReadContent {
  if (issues.length === 0) {
    return EMPTY_JIRA_ISSUE_CONTENT;
  }

  return Object.freeze({
    kind: 'jira-issue',
    issues: Object.freeze(
      issues.map((issue) =>
        Object.freeze({
          key: issue.key,
          issueKey: issue.issueKey,
          resolved: false,
          authenticationUrl: undefined,
          summary: undefined,
          url: undefined,
          type: undefined,
          typeIconUrl: undefined,
          statusName: undefined,
          statusCategory: undefined,
          ...resolvedByIssueKey.get(issue.issueKey)
        })
      )
    )
  });
}

/**
 * Compiles Jira site identity, compatible values, and bounded issue reads.
 */
export type JiraIssueReadRenderer = (content: JiraIssueReadContent) => ReactNode;

export function createJiraIssueFieldReadModule(
  options?: JiraIssueReadPolicy & { readonly render?: JiraIssueReadRenderer }
) {
  const { render, ...policy } = options ?? {};

  return defineFieldReadModule<JiraIssueReadContent>({
    fieldType: DatabaseFieldType.JIRA_ISSUE,
    render:
      render ??
      ((content) =>
        createElement(JiraIssueValueView, {
          content,
          authenticationLabel: policy.authenticationLabel
        })),
    compile(field: DatabaseField) {
      const cloudId = field.settings.cloudId;
      const serverId = field.settings.jiraServerId;

      function getIssues(
        storedValue: DatabaseValue | undefined,
        defaultValue: DatabaseValue | undefined
      ): readonly JiraIssueReference[] {
        const value = storedValue ?? defaultValue;

        return getJiraIssueReferences(value);
      }

      return Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ) {
          const issues = getIssues(storedValue, defaultValue);
          const cached = new Map<string, JiraIssueResourceValue>();

          for (const issue of issues) {
            const ready = policy.getJiraIssueSnapshot?.({
              cloudId,
              serverId,
              key: issue.issueKey
            });

            if (ready) {
              cached.set(issue.issueKey, ready);
            }
          }

          return createContent(issues, cached);
        },
        getResourceRead({ storedValue, defaultValue }) {
          if (!policy.getJiraIssueResource) {
            return undefined;
          }

          const issues = getIssues(storedValue, defaultValue);

          if (issues.length === 0) {
            return undefined;
          }

          const resourcesByIssueKey = new Map<
            string,
            FieldReadResource<JiraIssueResourceValue>
          >();
          const cached = new Map<string, JiraIssueResourceValue>();

          for (const issue of issues) {
            const ready = policy.getJiraIssueSnapshot?.({
              cloudId,
              serverId,
              key: issue.issueKey
            });

            if (ready) {
              cached.set(issue.issueKey, ready);
              continue;
            }

            if (!resourcesByIssueKey.has(issue.issueKey)) {
              resourcesByIssueKey.set(
                issue.issueKey,
                policy.getJiraIssueResource({ cloudId, serverId, key: issue.issueKey })
              );
            }
          }

          if (!resourcesByIssueKey.size) {
            return undefined;
          }

          const fallbackContent = createContent(issues, cached);

          return Object.freeze({
            resources: Object.freeze([...resourcesByIssueKey.values()]),
            resolve(
              read: <Value>(
                resource: FieldReadResource<Value>
              ) => FieldReadResourceState<Value>
            ) {
              const resolvedByIssueKey = new Map<string, JiraIssueResourceValue>(cached);

              for (const [issueKey, resource] of resourcesByIssueKey) {
                const state = read(resource);

                if (state.status === 'ready') {
                  resolvedByIssueKey.set(issueKey, state.value);
                }
              }

              return resolvedByIssueKey.size === 0
                ? fallbackContent
                : createContent(issues, resolvedByIssueKey);
            }
          });
        }
      });
    }
  });
}
